import { motion } from 'framer-motion'
import { SEO } from '@/components/SEO'
import { siteConfig } from '@/config/site'

export function Terms() {
  return (
    <>
      <SEO
        title="Terms of Service"
        description="NEXAIGEN Terms of Service - Terms and conditions for using our website and services."
        url="https://nexaigen.co.uk/terms"
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="pt-32 pb-20 container bg-background min-h-screen"
      >
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-8 text-white">Terms of Service</h1>
          <p className="text-slate-400 mb-12">Last updated: December 2024</p>

          <div className="prose prose-invert prose-slate max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">1. Introduction</h2>
              <p className="text-slate-400 leading-relaxed">
                Welcome to NEXAIGEN. These Terms of Service ("Terms") govern your use of our website nexaigen.co.uk and any services we provide. By accessing our website or using our services, you agree to be bound by these Terms.
              </p>
              <p className="text-slate-400 leading-relaxed mt-4">
                NEXAIGEN provides web design, mobile app development, SEO, and digital marketing services to businesses in the United Kingdom.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">2. Definitions</h2>
              <ul className="list-disc list-inside text-slate-400 space-y-2">
                <li><strong className="text-white">"We", "Us", "Our"</strong> refers to NEXAIGEN</li>
                <li><strong className="text-white">"You", "Your", "Client"</strong> refers to the user or customer of our services</li>
                <li><strong className="text-white">"Services"</strong> refers to website design, development, mobile apps, SEO, and digital marketing services</li>
                <li><strong className="text-white">"Website"</strong> refers to nexaigen.co.uk</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">3. Use of Website</h2>
              <p className="text-slate-400 leading-relaxed">
                You may use our website for lawful purposes only. You agree not to:
              </p>
              <ul className="list-disc list-inside text-slate-400 mt-4 space-y-2">
                <li>Use the website in any way that violates applicable laws or regulations</li>
                <li>Attempt to gain unauthorised access to our systems or networks</li>
                <li>Transmit any malicious code, viruses, or harmful content</li>
                <li>Copy, reproduce, or distribute our content without permission</li>
                <li>Use automated systems to access the website without our consent</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">4. Our Services</h2>
              <h3 className="text-xl font-semibold text-white mb-3">Quotations</h3>
              <p className="text-slate-400 leading-relaxed">
                All quotations provided are valid for 30 days unless otherwise stated. Prices are subject to change based on project requirements.
              </p>

              <h3 className="text-xl font-semibold text-white mb-3 mt-6">Project Agreements</h3>
              <p className="text-slate-400 leading-relaxed">
                Before commencing any work, we will provide a detailed proposal outlining the scope, timeline, and costs. A separate project agreement may be required for larger projects.
              </p>

              <h3 className="text-xl font-semibold text-white mb-3 mt-6">Payment Terms</h3>
              <ul className="list-disc list-inside text-slate-400 mt-4 space-y-2">
                <li>A deposit may be required before work commences</li>
                <li>Payment terms are typically 50% upfront, 50% on completion (unless otherwise agreed)</li>
                <li>Invoices are payable within 14 days of issue</li>
                <li>Late payments may incur interest at 8% above the Bank of England base rate</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">5. Client Responsibilities</h2>
              <p className="text-slate-400 leading-relaxed">
                To ensure successful project delivery, you agree to:
              </p>
              <ul className="list-disc list-inside text-slate-400 mt-4 space-y-2">
                <li>Provide all necessary content, images, and information in a timely manner</li>
                <li>Respond to queries and provide feedback within reasonable timeframes</li>
                <li>Ensure you have the rights to use any content you provide</li>
                <li>Make payments according to the agreed schedule</li>
                <li>Review and approve work within the agreed timeframes</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">6. Intellectual Property</h2>
              <h3 className="text-xl font-semibold text-white mb-3">Our Intellectual Property</h3>
              <p className="text-slate-400 leading-relaxed">
                All content on our website, including text, graphics, logos, and software, is owned by NEXAIGEN and protected by UK copyright laws.
              </p>

              <h3 className="text-xl font-semibold text-white mb-3 mt-6">Client Projects</h3>
              <p className="text-slate-400 leading-relaxed">
                Upon full payment, you will own the final deliverables we create for you. We retain the right to showcase completed projects in our portfolio unless otherwise agreed.
              </p>

              <h3 className="text-xl font-semibold text-white mb-3 mt-6">Third-Party Assets</h3>
              <p className="text-slate-400 leading-relaxed">
                Some projects may include third-party assets (fonts, images, plugins) which remain subject to their respective licences.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">7. Revisions and Changes</h2>
              <p className="text-slate-400 leading-relaxed">
                Our quotations include a specified number of revision rounds. Additional revisions or changes to scope may incur extra charges. Significant changes to project requirements after work has begun may require a revised quotation.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">8. Warranties and Disclaimers</h2>
              <p className="text-slate-400 leading-relaxed">
                We strive to deliver high-quality work and will correct any defects in our deliverables within 30 days of project completion at no extra cost.
              </p>
              <p className="text-slate-400 leading-relaxed mt-4">
                However, we do not guarantee:
              </p>
              <ul className="list-disc list-inside text-slate-400 mt-4 space-y-2">
                <li>Specific search engine rankings (SEO services)</li>
                <li>Specific sales or conversion rates</li>
                <li>Uninterrupted website availability (hosting is subject to third-party providers)</li>
                <li>Compatibility with future software or browser updates</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">9. Limitation of Liability</h2>
              <p className="text-slate-400 leading-relaxed">
                To the maximum extent permitted by law:
              </p>
              <ul className="list-disc list-inside text-slate-400 mt-4 space-y-2">
                <li>Our total liability shall not exceed the amount paid for the relevant service</li>
                <li>We are not liable for indirect, consequential, or incidental damages</li>
                <li>We are not liable for any loss of profits, data, or business opportunities</li>
              </ul>
              <p className="text-slate-400 leading-relaxed mt-4">
                Nothing in these Terms limits liability for death, personal injury, or fraud.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">10. Cancellation and Refunds</h2>
              <p className="text-slate-400 leading-relaxed">
                Either party may terminate a project with written notice. In the event of cancellation:
              </p>
              <ul className="list-disc list-inside text-slate-400 mt-4 space-y-2">
                <li>You will be charged for work completed up to the cancellation date</li>
                <li>Deposits are non-refundable once work has commenced</li>
                <li>Any materials or work completed will be delivered to you</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">11. Confidentiality</h2>
              <p className="text-slate-400 leading-relaxed">
                Both parties agree to keep confidential any proprietary or sensitive information shared during the course of the project. This obligation continues after the project ends.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">12. Force Majeure</h2>
              <p className="text-slate-400 leading-relaxed">
                Neither party shall be liable for delays or failure to perform due to circumstances beyond reasonable control, including natural disasters, war, terrorism, strikes, or government actions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">13. Governing Law</h2>
              <p className="text-slate-400 leading-relaxed">
                These Terms are governed by the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">14. Changes to Terms</h2>
              <p className="text-slate-400 leading-relaxed">
                We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to our website. Continued use of our services constitutes acceptance of the updated Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">15. Contact Us</h2>
              <p className="text-slate-400 leading-relaxed">
                If you have any questions about these Terms, please contact us:
              </p>
              <div className="mt-4 text-slate-400 space-y-2">
                <p><strong className="text-white">Email:</strong> {siteConfig.email}</p>
                <p><strong className="text-white">Phone:</strong> {siteConfig.phone}</p>
                <p><strong className="text-white">Location:</strong> {siteConfig.address.city}, {siteConfig.address.country}</p>
              </div>
            </section>
          </div>
        </div>
      </motion.div>
    </>
  )
}

export default Terms
